import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  terminateSessionAndRedirect,
} from "@/lib/api-client";
import { clearTokenExpiry, getRemainingAccessSec, recordTokenExpiry } from "@/lib/auth-session-expiry";
import { TOKEN_REFRESHED_EVENT } from "@/lib/ws-events";

export const ACCESS_REFRESH_LEAD_SEC = 90;
export const ACCESS_REFRESH_ON_VISIBLE_SEC = 120;

const PROACTIVE_RETRY_DELAY_MS = 5_000;
const BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : "/api/v1";

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;
let proactiveTimerId: ReturnType<typeof setTimeout> | null = null;
let visibilityListenerAttached = false;
let coordinatorActive = false;
let skipAutoRedirectOnFailure = false;
let onTokenRefreshed:
  | ((accessToken: string, refreshToken: string | null, expiresIn: number) => void)
  | null = null;

export function setCoordinatorOnTokenRefreshed(
  cb: ((accessToken: string, refreshToken: string | null, expiresIn: number) => void) | null,
): void {
  onTokenRefreshed = cb;
}

export function setCoordinatorSkipAutoRedirect(skip: boolean): void {
  skipAutoRedirectOnFailure = skip;
}

export type RefreshAccessTokenOptions = {
  skipAutoRedirect?: boolean;
  /** Proactive path: one retry after 5s before terminal logout. */
  proactive?: boolean;
};

function applyRefreshSuccess(
  accessToken: string,
  newRefresh: string | null,
  expiresIn: number,
): void {
  setAccessToken(accessToken);
  if (newRefresh) setRefreshToken(newRefresh);
  recordTokenExpiry(expiresIn);
  if (onTokenRefreshed) onTokenRefreshed(accessToken, newRefresh, expiresIn);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(TOKEN_REFRESHED_EVENT, { detail: { accessToken } }),
    );
  }
}

async function performRefreshRequest(): Promise<boolean> {
  try {
    const refreshToken = getRefreshToken();
    const body = refreshToken ? JSON.stringify({ refreshToken }) : JSON.stringify({});
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      credentials: "include",
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (data.success && data.data?.accessToken) {
      const newAccess = data.data.accessToken as string;
      const newRefresh = (data.data.refreshToken as string | undefined) || null;
      const expiresIn =
        typeof data.data.expiresIn === "number" && data.data.expiresIn > 0
          ? data.data.expiresIn
          : 900;
      applyRefreshSuccess(newAccess, newRefresh, expiresIn);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export async function refreshAccessToken(
  options: RefreshAccessTokenOptions = {},
): Promise<boolean> {
  const skipRedirect = options.skipAutoRedirect ?? skipAutoRedirectOnFailure;

  if (isRefreshing && refreshPromise) return refreshPromise;

  isRefreshing = true;
  refreshPromise = (async () => {
    let ok = await performRefreshRequest();
    if (!ok && options.proactive) {
      await new Promise((r) => setTimeout(r, PROACTIVE_RETRY_DELAY_MS));
      ok = await performRefreshRequest();
    }

    if (ok) {
      if (coordinatorActive) scheduleProactiveRefresh();
      return true;
    }

    if (!skipRedirect) {
      terminateSessionAndRedirect();
    }
    return false;
  })().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
}

function msUntilProactiveRefresh(): number | null {
  const remainingSec = getRemainingAccessSec();
  if (remainingSec == null) return null;
  const delaySec = remainingSec - ACCESS_REFRESH_LEAD_SEC;
  if (delaySec <= 0) return 0;
  return delaySec * 1000;
}

export function cancelProactiveRefresh(): void {
  if (proactiveTimerId != null) {
    clearTimeout(proactiveTimerId);
    proactiveTimerId = null;
  }
}

export function scheduleProactiveRefresh(): void {
  cancelProactiveRefresh();
  if (!coordinatorActive || !getAccessToken()) return;

  const delayMs = msUntilProactiveRefresh();
  if (delayMs == null) return;

  // BE-36: arm the proactive refresh even when the tab is hidden. Gating on
  // `document.hidden` here meant a backgrounded/idle tab never scheduled a
  // refresh timer, so the access token silently expired at its 15m TTL and the
  // next request (or the visibility catch-up) raced into a forced logout —
  // defeating the BE-26 "refresh before expiry" intent and Remember me. Browser
  // background timers still fire (throttled), and the 7-day refresh token keeps a
  // late refresh valid, so the session stays alive while idle.
  proactiveTimerId = setTimeout(() => {
    proactiveTimerId = null;
    void runProactiveRefreshIfNeeded();
  }, delayMs);
}

export function runProactiveRefreshIfNeeded(): void {
  if (!coordinatorActive || !getAccessToken()) return;

  const remaining = getRemainingAccessSec();
  if (remaining == null) return;

  const shouldRefreshNow =
    remaining <= ACCESS_REFRESH_LEAD_SEC ||
    (typeof document !== "undefined" &&
      !document.hidden &&
      remaining < ACCESS_REFRESH_ON_VISIBLE_SEC);

  if (!shouldRefreshNow) {
    scheduleProactiveRefresh();
    return;
  }

  void refreshAccessToken({ proactive: true, skipAutoRedirect: skipAutoRedirectOnFailure });
}

function onVisibilityChange(): void {
  if (typeof document === "undefined") return;
  // BE-36: keep the proactive timer armed while hidden so an idle background tab
  // still refreshes before the access token expires. (Previously, hiding the tab
  // cancelled the timer, so a 15m+ idle stint logged the operator out despite
  // Remember me.) On unhide we still catch up immediately if expiry is near.
  if (document.hidden) return;

  const remaining = getRemainingAccessSec();
  if (remaining != null && remaining < ACCESS_REFRESH_ON_VISIBLE_SEC) {
    void runProactiveRefreshIfNeeded();
    return;
  }
  scheduleProactiveRefresh();
}

function attachVisibilityListener(): void {
  if (visibilityListenerAttached || typeof document === "undefined") return;
  document.addEventListener("visibilitychange", onVisibilityChange);
  visibilityListenerAttached = true;
}

function detachVisibilityListener(): void {
  if (!visibilityListenerAttached || typeof document === "undefined") return;
  document.removeEventListener("visibilitychange", onVisibilityChange);
  visibilityListenerAttached = false;
}

/** Start proactive scheduling and visibility handling. */
export function startAuthRefreshCoordinator(): void {
  coordinatorActive = true;
  attachVisibilityListener();
  scheduleProactiveRefresh();
}

/** Stop timers and visibility handling (logout / session terminated). */
export function stopAuthRefreshCoordinator(): void {
  coordinatorActive = false;
  cancelProactiveRefresh();
  detachVisibilityListener();
  clearTokenExpiry();
}

/** Vitest-only: clear in-flight refresh state between cases. */
export function resetRefreshCoordinatorForTests(): void {
  isRefreshing = false;
  refreshPromise = null;
  cancelProactiveRefresh();
}
